import { memo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface ITimePickerProps {
  value: string; // HH:MM format
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const TimePicker = ({ value, onChange, disabled = false, className }: ITimePickerProps) => {
  const [hours, minutes] = value.split(':');

  const handleHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newHours = e.target.value.replace(/[^0-9]/g, '');

      // Limit to 2 digits
      if (newHours.length > 2) {
        newHours = newHours.slice(0, 2);
      }

      // Validate range 0-23
      const hoursNum = parseInt(newHours || '0', 10);
      if (hoursNum > 23) {
        newHours = '23';
      }

      // Don't pad during typing - only on blur
      onChange(`${newHours}:${minutes}`);
    },
    [minutes, onChange]
  );

  const handleMinutesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newMinutes = e.target.value.replace(/[^0-9]/g, '');

      // Limit to 2 digits
      if (newMinutes.length > 2) {
        newMinutes = newMinutes.slice(0, 2);
      }

      // Validate range 0-59
      const minutesNum = parseInt(newMinutes || '0', 10);
      if (minutesNum > 59) {
        newMinutes = '59';
      }

      // Don't pad during typing - only on blur
      onChange(`${hours}:${newMinutes}`);
    },
    [hours, onChange]
  );

  const handleHoursBlur = useCallback(() => {
    // Ensure hours is always 2 digits on blur
    const hoursNum = parseInt(hours || '0', 10);
    if (hoursNum < 10 && hours.length === 1) {
      onChange(`${hours.padStart(2, '0')}:${minutes}`);
    }
  }, [hours, minutes, onChange]);

  const handleMinutesBlur = useCallback(() => {
    // Ensure minutes is always 2 digits on blur
    const minutesNum = parseInt(minutes || '0', 10);
    if (minutesNum < 10 && minutes.length === 1) {
      onChange(`${hours}:${minutes.padStart(2, '0')}`);
    }
  }, [hours, minutes, onChange]);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={hours}
        onChange={handleHoursChange}
        onBlur={handleHoursBlur}
        disabled={disabled}
        placeholder="HH"
        maxLength={2}
        className="w-16 text-center font-mono text-lg"
      />
      <span className="text-2xl font-semibold text-gray-400">:</span>
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={minutes}
        onChange={handleMinutesChange}
        onBlur={handleMinutesBlur}
        disabled={disabled}
        placeholder="MM"
        maxLength={2}
        className="w-16 text-center font-mono text-lg"
      />
      <span className="text-sm text-gray-500 ml-1">24h</span>
    </div>
  );
};

export const TimePickerMemo = memo(TimePicker);
export { TimePicker };
